import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const num = (v) => (v || v === 0 ? Number(v).toLocaleString() : "—");
const parse = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const ROWS = [
  ["Population (2019 census)", (c) => c.census?.population, num],
  ["Women per man", (c) => c.census?.women_per_man, (v) => (v ? Number(v).toFixed(3) : "—")],
  ["Households", (c) => c.census?.households, num],
  ["People per km²", (c) => c.census?.density, (v) => (v ? num(Math.round(v)) : "—")],
  ["Youth aged 15–24", (c) => parse(c.youth_population), (v) => (v ? num(v) : "Researching")],
  ["In secondary school", (c) => parse(c.secondary_enrolment), (v) => (v ? num(v) : "Researching")],
  ["In primary school", (c) => parse(c.primary_enrolment), (v) => (v ? num(v) : "Researching")],
  ["GDP (PPP) estimate", (c) => parse(c.gdp_ppp_usd_bn), (v) => (v ? `$${v}bn` : "Researching")],
  ["Infrastructure projects tracked", (c) => (c.projects || []).length, num],
  ["Proposals received", (c) => c.proposal_count || 0, num],
];

const Picker = ({ counties, value, onChange, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full border border-white/15 bg-black px-4 py-3 font-display text-lg uppercase tracking-tight text-white focus:border-ochre focus:outline-none"
  >
    {counties.map((c) => (
      <option key={c.name} value={c.name}>
        {c.name}
      </option>
    ))}
  </select>
);

export const CountyCompare = ({ counties = [] }) => {
  const sorted = useMemo(() => [...counties].sort((a, b) => a.name.localeCompare(b.name)), [counties]);
  const [left, setLeft] = useState("Nairobi");
  const [right, setRight] = useState("Kisumu");
  const a = sorted.find((c) => c.name === left);
  const b = sorted.find((c) => c.name === right);

  if (!sorted.length) return null;

  return (
    <div data-testid="county-compare">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <p className="overline text-maasai">County A</p>
          <div className="mt-3">
            <Picker counties={sorted} value={left} onChange={setLeft} testId="compare-select-a" />
          </div>
        </div>
        <div>
          <p className="overline text-ochre">County B</p>
          <div className="mt-3">
            <Picker counties={sorted} value={right} onChange={setRight} testId="compare-select-b" />
          </div>
        </div>
      </div>

      <div className="mt-8 border border-white/10">
        <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-white/15 bg-[#0b0b0b]">
          <span className="px-4 py-3 text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">Measure</span>
          <span className="px-4 py-3 font-display text-sm uppercase tracking-tight text-maasai">{a?.name}</span>
          <span className="px-4 py-3 font-display text-sm uppercase tracking-tight text-ochre">{b?.name}</span>
        </div>
        {ROWS.map(([label, get, fmt], i) => {
          const av = get(a || {});
          const bv = get(b || {});
          const aWins = Number(av) > Number(bv);
          const bWins = Number(bv) > Number(av);
          return (
            <div
              key={label}
              className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-white/5 last:border-0"
              data-testid={`compare-row-${i}`}
            >
              <span className="px-4 py-3 text-[0.68rem] uppercase tracking-[0.12em] text-zinc-500">{label}</span>
              <span className={`mono px-4 py-3 text-sm ${aWins ? "text-white" : "text-zinc-500"}`}>
                {fmt(av)}
                {aWins ? <span className="ml-2 text-maasai">▲</span> : null}
              </span>
              <span className={`mono px-4 py-3 text-sm ${bWins ? "text-white" : "text-zinc-500"}`}>
                {fmt(bv)}
                {bWins ? <span className="ml-2 text-ochre">▲</span> : null}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {[a, b].map((c, i) => (
          <div key={c?.name || i} className="border border-white/10 bg-[#0b0b0b] p-5" data-testid={`compare-status-${i}`}>
            <p className="overline" style={{ color: i ? "#E5A91E" : "#E32118" }}>
              {c?.name} · {c?.status}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400">{c?.note}</p>
            {c?.programmes ? (
              <p className="mt-3 text-xs uppercase tracking-[0.14em] text-zinc-400">{c.programmes}</p>
            ) : (
              <p className="mt-3 text-xs uppercase tracking-[0.14em] text-zinc-400" data-testid={`compare-programmes-empty-${i}`}>
                No programmes proposed yet — open to proposals
              </p>
            )}
            {c?.priority ? (
              <p className="mt-4 inline-block border border-ochre px-2 py-1 text-[0.52rem] font-bold uppercase tracking-[0.14em] text-ochre">
                Priority 18 county
              </p>
            ) : null}
          </div>
        ))}
      </div>

      <p className="mt-6 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
        Population, households and density are 2019 census actuals. Youth, schooling, GDP (PPP) and projects are
        researched estimates and are still being filled in county by county.{" "}
        <Link to="/apply" className="text-ochre underline underline-offset-4">
          Send better data
        </Link>
        .
      </p>
    </div>
  );
};
